import { Link } from 'react-router-dom';
import { Shield, CheckCircle, Camera, Monitor, Wifi, HardDrive, Wrench, Smartphone, Cpu, ScanEye, Clock, FileCheck, Radio } from 'lucide-react';

const dispatch = "https://images.unsplash.com/photo-1719400471588-575b23e27bd7?w=800";

const cctvServices = [
  {
    icon: <Camera className="h-6 w-6" />,
    title: 'Professional System Design',
    description: 'Our technicians conduct comprehensive site surveys to map your property, identify coverage gaps, and design a camera system that eliminates blind spots. We engineer solutions using IP, analog, or hybrid systems based on your infrastructure, budget, and security objectives — not cookie-cutter packages.',
    ideal: 'Custom designed for every property type and size.'
  },
  {
    icon: <ScanEye className="h-6 w-6" />,
    title: 'HD & 4K IP Camera Installation',
    description: 'We install commercial-grade IP cameras with features including night vision, wide dynamic range, vandal-resistant housings, PTZ (pan-tilt-zoom) capability, and weatherproof ratings. All cameras are strategically positioned for optimal coverage of entrances, exits, parking areas, and high-value zones.',
    ideal: 'Ideal for retail, warehouses, offices, and multi-site deployments.'
  },
  {
    icon: <Monitor className="h-6 w-6" />,
    title: '24/7 Live Monitoring Center',
    description: 'Our professional monitoring center provides real-time surveillance of your property around the clock. Trained operators watch your camera feeds, detect suspicious activity, and initiate immediate response protocols — dispatching security personnel, contacting law enforcement, or alerting you directly when incidents occur.',
    ideal: 'Essential for properties requiring active surveillance and rapid response.'
  },
  {
    icon: <Smartphone className="h-6 w-6" />,
    title: 'Remote Viewing Access',
    description: 'View your cameras live from anywhere using your smartphone, tablet, or computer. Our systems include secure remote access with multi-user login, playback capabilities, and push notifications for motion-triggered events. Check on your property anytime, from anywhere in the world.',
    ideal: 'Included with every installation for complete visibility.'
  },
  {
    icon: <Cpu className="h-6 w-6" />,
    title: 'Video Analytics & Smart Detection',
    description: 'Advanced camera analytics including motion detection zones, line-crossing alerts, loitering detection, facial recognition capability, and license plate capture. Smart detection reduces false alarms and ensures monitoring operators focus on genuine security events rather than environmental triggers.',
    ideal: 'Recommended for high-traffic areas and critical infrastructure.'
  },
  {
    icon: <HardDrive className="h-6 w-6" />,
    title: 'DVR/NVR Recording Systems',
    description: 'All footage is recorded to enterprise-grade DVR or NVR systems with customizable retention periods — from 30 days to 1 year or more depending on your compliance requirements and storage capacity. Redundant storage options available for critical facilities requiring data preservation.',
    ideal: 'Retention periods customized to your compliance and insurance needs.'
  },
  {
    icon: <Wrench className="h-6 w-6" />,
    title: 'Maintenance & Support Plans',
    description: 'We do not just install and walk away. Our ongoing maintenance plans include regular system health checks, firmware updates, camera cleaning and repositioning, recording verification, and priority support. When a camera goes down, we respond — not next week, but now.',
    ideal: 'Available as monthly or annual service contracts.'
  },
  {
    icon: <Wifi className="h-6 w-6" />,
    title: 'Network & Infrastructure Setup',
    description: 'Proper camera systems require proper networking. We handle all cabling, PoE (Power over Ethernet) switching, network configuration, bandwidth optimization, and cybersecurity hardening. Our installations are built for reliability and protected against unauthorized access.',
    ideal: 'Critical for large-scale deployments and enterprise environments.'
  }
];

const monitoringFeatures = [
  { icon: <Shield className="h-5 w-5" />, text: 'Licensed Security Monitoring Operators' },
  { icon: <Clock className="h-5 w-5" />, text: '24/7/365 Live Monitoring Coverage' },
  { icon: <Radio className="h-5 w-5" />, text: 'Immediate Dispatch & Law Enforcement Coordination' },
  { icon: <FileCheck className="h-5 w-5" />, text: 'Incident Documentation & Reporting' },
  { icon: <CheckCircle className="h-5 w-5" />, text: 'Redundant Systems with Backup Power' },
  { icon: <Smartphone className="h-5 w-5" />, text: 'Client Notification via Call, Text, or App' }
];

export const CCTVSection = () => {
  return (
    <div id="cctv-monitoring" className="py-20 bg-[#121212]" data-testid="service-section-cctv-monitoring">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div className="lg:order-2">
            <p className="text-[#F5C518] text-sm font-mono uppercase tracking-widest mb-2">24/7 Video Surveillance</p>
            <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
              CCTV Monitoring.<br /><span className="text-[#F5C518]">Eyes That Never Close.</span>
            </h2>
            <p className="text-zinc-400 leading-relaxed mb-6">
              From system design and professional installation to 24/7 live monitoring from our dispatch center — we deliver complete CCTV solutions that actually work. We install, we maintain, and we watch. Our monitoring operators respond to events in real time, ensuring your cameras are not just recording — they are actively protecting your property.
            </p>
            <div className="bg-[#0A0A0A] border-l-4 border-[#F5C518] p-4 mb-6">
              <p className="text-white font-semibold">Cameras without monitoring are just decoration.</p>
              <p className="text-[#F5C518]">We make sure someone is always watching.</p>
            </div>
            <Link to="/contact" className="btn-skew inline-block bg-[#F5C518] text-black font-bold uppercase tracking-wider px-6 py-3 hover:bg-[#E5B508] transition-all duration-300" data-testid="service-cta-cctv-monitoring">
              <span>Request CCTV Consultation</span>
            </Link>
          </div>
          <div className="lg:order-1">
            <div className="relative">
              <img src={dispatch} alt="CCTV Monitoring Center" className="w-full aspect-[4/3] object-cover border border-zinc-800" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#121212]/50 to-transparent" />
            </div>
          </div>
        </div>

        {/* Service Capabilities */}
        <div className="mb-20">
          <h3 className="text-2xl font-bold uppercase tracking-tight text-white mb-8 font-['Barlow_Condensed'] text-center">
            CCTV <span className="text-[#F5C518]">Solutions</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {cctvServices.map((service) => (
              <div key={service.title} className="bg-[#0A0A0A] border border-zinc-800 p-6 hover:border-[#F5C518]/50 transition-colors">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-[#F5C518]/10 text-[#F5C518] mb-4">
                  {service.icon}
                </div>
                <h4 className="text-lg font-bold text-white mb-3 font-['Barlow_Condensed'] uppercase">{service.title}</h4>
                <p className="text-zinc-400 text-sm mb-3">{service.description}</p>
                <p className="text-[#F5C518] text-xs font-semibold">{service.ideal}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Monitoring Features & Process */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          <div className="bg-[#0A0A0A] border border-zinc-800 p-8">
            <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-6 font-['Barlow_Condensed']">
              Monitoring <span className="text-[#F5C518]">Center</span>
            </h3>
            <div className="space-y-4">
              {monitoringFeatures.map((item) => (
                <div key={item.text} className="flex items-center text-zinc-400">
                  <span className="text-[#F5C518] mr-3">{item.icon}</span>
                  {item.text}
                </div>
              ))}
            </div>
            <p className="text-zinc-500 text-sm mt-6 pt-6 border-t border-zinc-800">
              Our monitoring center is the nerve center of your security system — staffed, equipped, and always active.
            </p>
          </div>

          <div className="bg-[#0A0A0A] border border-zinc-800 p-8">
            <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-6 font-['Barlow_Condensed']">
              Our <span className="text-[#F5C518]">Process</span>
            </h3>
            <p className="text-zinc-400 mb-4">From consultation to ongoing protection:</p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Free on-site survey and security assessment
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Custom system design with camera placement map
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Professional installation by licensed technicians
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                System configuration, testing, and client training
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                24/7 monitoring activation and dispatch integration
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Ongoing maintenance, support, and system upgrades
              </li>
            </ul>
            <p className="text-[#F5C518] text-sm font-semibold">
              We handle everything. You just watch the results.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-[#0A0A0A] border border-[#F5C518]/30 p-8 text-center">
          <h3 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
            See Everything. <span className="text-[#F5C518]">Miss Nothing.</span>
          </h3>
          <p className="text-zinc-400 mb-6 max-w-2xl mx-auto">
            Schedule a free site survey and get a custom CCTV proposal with camera placement, equipment specs, and monitoring options.
          </p>
          <Link to="/contact" className="btn-skew inline-block bg-[#F5C518] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#E5B508] transition-all duration-300">
            <span>Get CCTV Quote</span>
          </Link>
        </div>
      </div>
    </div>
  );
};
